import React, { useEffect, useRef, useState } from 'react';
import Select from '../UI/Select';
import styled from 'styled-components';
import { SelectOption } from '@/types/shared';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import Btn from '../UI/Btn';
import { TableStore } from '@/pages/Admin/Stores';
import { importVoucherCSV } from '@/api/voucher';
import { toast } from 'react-toastify';
import { useUIStore } from '@/store/useUIStore';
import axios from 'axios';

// Import assets directly
import csvIcon from '/icons/csv.svg';

interface Props {
   sample: string;
}

// type SelectOption = {
//    _id: string;
//    name: string;
// };

interface RateLimitError {
   status: number;
   message: string;
   retryAfter?: number;
   currentCount?: number;
   attemptedCount?: number;
   err: string;
   msg: string;
}

const ImportVoucher = ({ data }: { data: TableStore[] }) => {
   const [selectedUpload, setSelectedUpload] = useState<File | null>(null);
   const [selectedStore, setSelectedStore] = useState<string>('');
   const [isLoading, setIsLoading] = useState(false);
   const { handleClose, handleRefresh } = useUIStore();

   const fileInputRef = useRef<HTMLInputElement | null>(null);

   const handleUploadClick = () => {
      if (fileInputRef.current) {
         fileInputRef.current.click();
      }
   };

   useEffect(() => {
      if (data && data.length > 0) {
         setSelectedStore(data[0]._id);
      }
      setSelectedUpload(null);
   }, [data]);

   const handleUploadCSV = async () => {
      if (!selectedUpload) {
         toast.error('Please select a file', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
         });
         return;
      }

      if (!selectedStore) {
         toast.error('Please select a store', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
         });
         return;
      }

      try {
         setIsLoading(true);
         const response = await importVoucherCSV(selectedUpload, selectedStore);

         toast.success(response.msg, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
         });

         handleRefresh();
         handleClose();

         // Reset form
         setSelectedUpload(null);
         setSelectedStore('');
      } catch (err: unknown) {
         console.error('Error importing vouchers:', err);

         // Handle rate limit exceeded error (429)
         if (
            typeof err === 'object' &&
            err !== null &&
            'status' in err &&
            err.status === 429
         ) {
            const rateLimitError = err as RateLimitError;
            const retryAfter = rateLimitError.retryAfter || 60; // Default to 60 seconds if not provided
            const minutes = Math.floor(retryAfter / 60);
            const seconds = retryAfter % 60;
            const timeMessage =
               minutes > 0
                  ? `${minutes} minute${
                       minutes > 1 ? 's' : ''
                    } and ${seconds} second${seconds !== 1 ? 's' : ''}`
                  : `${seconds} second${seconds !== 1 ? 's' : ''}`;

            // Display the error message from the backend
            toast.warning(
               `Too many actions at once. Try again in ${timeMessage}.`,
               {
                  position: 'top-right',
                  autoClose: 8000,
                  hideProgressBar: false,
               }
            );
         } else if (axios.isAxiosError(err)) {
            // console.log(err);
            // Handle other Axios errors
            toast.error(
               err.response?.data?.msg || 'Failed to import vouchers',
               {
                  position: 'top-right',
                  autoClose: 5000,
                  hideProgressBar: false,
               }
            );
         } else {
            // Handle other errors
            toast.error('Failed to import vouchers', {
               position: 'top-right',
               autoClose: 5000,
               hideProgressBar: false,
            });
         }
      } finally {
         setIsLoading(false);
      }
   };

   const handleDownloadSample = () => {
      // Two columns only: code, amount. No header row.
      const sampleCSV =
         'SMF000001,50\nSMF000002,80\nSMF000003,100\nSMF000004,150';

      // Create a blob and download link
      const blob = new Blob([sampleCSV], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'sample.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
   };

   return (
      <Wrapper>
         <Select
            title="Select Store"
            data={data.map((item: TableStore) => ({
               _id: item._id,
               name: item.storename,
            }))}
            onChange={(value) => {
               setSelectedStore(value._id);
            }}
         />

         <UploadContent onClick={handleUploadClick}>
            <Input
               type="file"
               hidden
               accept=".csv"
               ref={fileInputRef}
               onChange={(e) => {
                  const files = e.target.files || [];
                  if (files.length > 0) {
                     setSelectedUpload(files[0]);
                     console.log(
                        'Files selected:',
                        Array.from(files).map((f) => f.name)
                     );
                     // Handle file upload logic here
                  }
               }}
            />
            <div>
               {selectedUpload !== null ? (
                  <ImgWrapper>
                     <IconsLib
                        icon="checkbox-circle-fill"
                        size="45px"
                        color={colors.SUCCESS}
                     />
                  </ImgWrapper>
               ) : (
                  <ImgWrapper>
                     <Img src={csvIcon} alt="CSV Icon" />
                  </ImgWrapper>
               )}

               {selectedUpload !== null ? (
                  <UploadText>
                     You imported a CSV file: {selectedUpload.name}
                  </UploadText>
               ) : (
                  <UploadText>Select a CSV file to import</UploadText>
               )}
            </div>
         </UploadContent>

         {selectedUpload !== null && (
            <ActionWrapper>
               <Btn
                  title={isLoading ? 'Uploading...' : 'Upload'}
                  type={isLoading ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                  textcolor="#fff"
                  hasIcon={isLoading}
                  icon="loader"
                  iconPosition="right"
                  onClick={handleUploadCSV}
               />
            </ActionWrapper>
         )}

         <Instruction>
            <Label>Follow format below or</Label>
            <Labellink onClick={handleDownloadSample}>
               Download sample.csv
            </Labellink>
         </Instruction>

         <SheetHint>
            CSV must have <Strong>2 columns only</Strong> — no header row.
            Column A is the voucher code, Column B is the amount.
         </SheetHint>
         <Sheet>
            <SheetCols>
               <SheetCol>
                  <SheetColTag>A</SheetColTag>
                  <SheetColName>Code</SheetColName>
               </SheetCol>
               <SheetCol>
                  <SheetColTag>B</SheetColTag>
                  <SheetColName>Amount</SheetColName>
               </SheetCol>
            </SheetCols>
            <SheetRow>
               <SheetCell>SMF000001</SheetCell>
               <SheetCell $num>50</SheetCell>
            </SheetRow>
            <SheetRow>
               <SheetCell>SMF000002</SheetCell>
               <SheetCell $num>80</SheetCell>
            </SheetRow>
            <SheetRow>
               <SheetCell>SMF000003</SheetCell>
               <SheetCell $num>100</SheetCell>
            </SheetRow>
            <SheetRow>
               <SheetCell>SMF000004</SheetCell>
               <SheetCell $num>150</SheetCell>
            </SheetRow>
         </Sheet>
      </Wrapper>
   );
};

const Wrapper = styled.div``;
const UploadContent = styled.div`
   background: ${colors.GRAY14};
   border-radius: 4px;
   display: flex;
   justify-content: center;
   align-items: center;
   margin-top: 1.5rem;
   width: 600px;
   padding: 1rem;
   border: 2px dashed ${colors.GRAY10};
   cursor: pointer;

   @media (max-width: 768px) {
      width: 100%;
   }
`;
const UploadText = styled.p`
   font-size: 14px;
   margin-top: 5px;
   color: ${colors.GRAY3};
`;

const ImgWrapper = styled.div`
   display: grid;
   place-items: center;
`;
const Img = styled.img`
   width: 40px;
`;

const Input = styled.input`
   position: absolute;
   margin: 0;
   padding: 0;
   width: 100%;
   height: 100%;
   outline: none;
   opacity: 0;
   cursor: pointer;
`;

const Instruction = styled.div`
   display: flex;
   justify-content: flex-start;
   align-items: center;
   margin-top: 1.5rem;
   margin-bottom: 8px;
   gap: 10px;
`;
const Label = styled.p`
   font-size: 14px;
   color: ${colors.GRAY3};
`;

const Labellink = styled.p`
   font-size: 14px;
   color: ${colors.GREEN_APPLE};
   cursor: pointer;
   text-decoration: underline;
`;

const ActionWrapper = styled.div`
   margin-top: 1rem;
   width: 100%;
`;

const SheetHint = styled.p`
   font-size: 12.5px;
   color: ${colors.GRAY3};
   line-height: 1.55;
   margin-bottom: 10px;
`;
const Strong = styled.span`
   color: ${colors.PRIMARY};
   font-weight: 700;
`;
const Sheet = styled.div`
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
   overflow: hidden;
   background: var(--app-surface);
   font-family: 'Courier New', Consolas, monospace;
   font-size: 13px;
`;
const SheetCols = styled.div`
   display: grid;
   grid-template-columns: 1fr 140px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
`;
const SheetCol = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 12px;
   &:first-child {
      border-right: 1px solid ${colors.GRAY13};
   }
`;
const SheetColTag = styled.span`
   display: inline-grid;
   place-items: center;
   width: 18px;
   height: 18px;
   border-radius: 4px;
   background: ${colors.PRIMARY};
   color: white;
   font-size: 11px;
   font-weight: 800;
`;
const SheetColName = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY1};
   text-transform: uppercase;
   letter-spacing: 0.08em;
`;
const SheetRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 140px;
   &:not(:last-child) {
      border-bottom: 1px solid ${colors.GRAY13};
   }
`;
const SheetCell = styled.div<{ $num?: boolean }>`
   padding: 8px 12px;
   color: ${colors.GRAY_DARK};
   &:first-child {
      border-right: 1px solid ${colors.GRAY13};
   }
   ${({ $num }) => $num && `text-align: right; color: ${colors.PRIMARY}; font-weight: 700;`}
`;

export default ImportVoucher;
